import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  ReactNode,
} from 'react'
import req from '@sportninja/common/api/request'
import { BillingScheduleItem } from '@sportninja/common/types/Registration'
import { billingReducer, initialState } from './reducer'
import { TeamBillingState } from './types'

interface BillingContextValue extends TeamBillingState {
  fetchBilling: (rosterId: string) => Promise<void>
}

const BillingContext = createContext<BillingContextValue | null>(null)

export function useBillingContext() {
  const ctx = useContext(BillingContext)
  if (!ctx) {
    throw new Error('useBillingContext must be used within BillingProvider')
  }
  return ctx
}

interface BillingProviderProps {
  children: ReactNode
  teamId: string
}

export function BillingProvider({ children, teamId }: BillingProviderProps) {
  const [state, dispatch] = useReducer(billingReducer, initialState)

  const fetchBilling = useCallback(
    async (rosterId: string) => {
      dispatch({ type: 'SET_BILLING_LOADING', payload: true })
      dispatch({ type: 'SET_ERROR', payload: null })
      try {
        const response = await req(
          `/teams/${teamId}/rosters/${rosterId}/billing`
        )
        const items: BillingScheduleItem[] = response.data
        dispatch({ type: 'SET_BILLING_ITEMS', payload: items })

        const code =
          items[0]?.currency_country?.currency_code ||
          items[0]?.currency_code ||
          'USD'
        dispatch({ type: 'SET_CURRENCY_CODE', payload: code })
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          payload: err instanceof Error ? err : new Error(String(err)),
        })
      } finally {
        dispatch({ type: 'SET_BILLING_LOADING', payload: false })
        dispatch({ type: 'SET_LOADING', payload: false })
      }
    },
    [teamId]
  )

  return (
    <BillingContext.Provider value={{ ...state, fetchBilling }}>
      {children}
    </BillingContext.Provider>
  )
}
